import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api-client';
import type { ChartingStatus, HoldPosition, MediaType, User, WorkflowStatus } from '@/types';

export interface ChartingQueueItem {
  id: string;
  holdRequestId: string;
  requestNumber: string;
  advertiser: string;
  salesAeName?: string;
  displayName: string;
  market: string;
  mediaType: MediaType;
  holdStartDate: string;
  holdExpirationDate: string;
  flightStartDate: string;
  flightEndDate: string;
  chartingStatus: ChartingStatus;
  workflowStatus: WorkflowStatus;
  assignedChartingUserId?: string;
  assignedChartingUserName?: string;
  assignedUserId?: string;
  assignedUserName?: string;
  assignedAt?: string;
  assignedBy?: string;
  daysUntilExpiration?: number;
  createdAt: string;
  commentCount?: number;
  qty: number;
  digitalOnlyNoOfSpots?: number;
}

export interface ChartingQueueFilters {
  chartingStatus?: ChartingStatus;
  chartingStatuses?: ChartingStatus[];
  workflowStatus?: WorkflowStatus;
  assignedChartingUserId?: string | null;
  assignedUserId?: string | null;
  market?: string;
  markets?: string[];
  mediaTypes?: string[];
  expiringWithinDays?: number;
  unassignedOnly?: boolean;
  includeExpired?: boolean;
  myAssignments?: boolean;
  currentUserId?: string;
  sortBy?: 'expiration' | 'created' | 'market' | 'detailed_status';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ChartingStatistics {
  totalOpen: number;
  totalInProgress: number;
  totalCompleted: number;
  myAssigned: number;
  expiringToday: number;
  expiringThisWeek: number;
  totalUnassigned: number;
  totalOverdue: number;
  totalExceptionsRequired: number;
}

/**
 * Hook to fetch charting work queue
 */
export function useChartingQueue(filters: ChartingQueueFilters = {}) {
  const queryParams = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if ((key === 'markets' || key === 'mediaTypes' || key === 'chartingStatuses') && Array.isArray(value)) {
        value.forEach(v => queryParams.append(key, v));
      } else {
        queryParams.append(key, String(value));
      }
    }
  });

  return useQuery({
    queryKey: ['chartingQueue', filters],
    queryFn: async () => {
      const response = await get<{
        success: boolean;
        data: {
          items: ChartingQueueItem[];
          pagination: {
            total: number;
            limit: number;
            offset: number;
            hasMore: boolean;
          };
        };
      }>(`/charting/queue?${queryParams.toString()}`);
      return response.data;
    },
    keepPreviousData: true,
  });
}

/**
 * Hook to fetch charting statistics
 */
export function useChartingStatistics() {
  return useQuery({
    queryKey: ['chartingStatistics'],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: ChartingStatistics }>('/charting/statistics');
      return response.data;
    },
  });
}

/**
 * Hook to assign line item to self
 */
export function useAssignToSelf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lineItemId: string) =>
      post<{ success: boolean; message: string }>('/charting/assign-to-self', { lineItemId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] }); // Invalidate detail view
    },
  });
}

/**
 * Hook to assign line item to another user (manager only)
 */
export function useAssignToUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, targetUserId }: { lineItemId: string; targetUserId: string }) =>
      post<{ success: boolean; message: string }>('/charting/assign-to-user', {
        lineItemId,
        targetUserId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] }); // Invalidate detail view
    },
  });
}

/**
 * Hook to bulk assign line items (manager only)
 */
export function useBulkAssign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignments: Array<{ lineItemId: string; userId: string }>) =>
      post<{ success: boolean; message: string }>('/charting/bulk-assign', { assignments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
    },
  });
}

/**
 * Hook to mark line item as completed
 */
export function useMarkCompleted() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, comments }: { lineItemId: string; comments?: string }) =>
      post<{ success: boolean; message: string }>('/charting/mark-completed', {
        lineItemId,
        comments,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] }); // Invalidate detail view
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
    },
  });
}

/**
 * Hook to require exception approval
 */
export function useRequireExceptionApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, reason }: { lineItemId: string; reason: string }) =>
      post<{ success: boolean; message: string }>('/charting/require-exception-approval', {
        lineItemId,
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] }); // Invalidate detail view
    },
  });
}

/**
 * Hook to unassign line item (manager only)
 */
export function useUnassignLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lineItemId: string) =>
      post<{ success: boolean; message: string }>('/charting/unassign', { lineItemId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] }); // Invalidate detail view
    },
  });
}

/**
 * Hook to update detailed status of a hold request
 */
export function useUpdateDetailedStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ holdRequestId, workflowStatus, comments }: { 
      holdRequestId: string; 
      workflowStatus: WorkflowStatus;
      comments?: string;
    }) =>
      post<{ success: boolean; message: string }>('/charting/update-detailed-status', {
        holdRequestId,
        workflowStatus,
        comments,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
    },
  });
}

/**
 * Hook to assign hold request to user (for detailed status management)
 */
export function useAssignHoldRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ holdRequestId, targetUserId }: { holdRequestId: string; targetUserId: string }) =>
      post<{ success: boolean; message: string }>('/charting/assign-hold-request', {
        holdRequestId,
        targetUserId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
    },
  });
}

/**
 * Hook to self-assign hold request
 */
export function useAssignHoldRequestToSelf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (holdRequestId: string) =>
      post<{ success: boolean; message: string }>('/charting/assign-hold-request-to-self', { holdRequestId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
    },
  });
}

/**
 * Hook to fetch charting team members
 */
export function useChartingTeamMembers() {
  return useQuery({
    queryKey: ['chartingTeamMembers'],
    queryFn: async () => {
      const [membersResponse, managersResponse] = await Promise.all([
        get<{ success: boolean; data: { users: User[] } }>('/users/role/charting_member'),
        get<{ success: boolean; data: { users: User[] } }>('/users/role/charting_manager'),
      ]);
      
      const members = membersResponse.data?.users || [];
      const managers = managersResponse.data?.users || [];
      
      return [...members, ...managers];
    },
  });
}

/**
 * Hook to update line item status with optional comment
 */
export function useUpdateLineItemStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      lineItemId, 
      chartingStatus, 
      comment 
    }: { 
      lineItemId: string; 
      chartingStatus: ChartingStatus; 
      comment?: string;
    }) =>
      post<{ success: boolean; message: string }>('/charting/update-line-item-status', {
        lineItemId,
        chartingStatus,
        comment,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemComments'] });
    },
  });
}

/**
 * Set or clear a line item's backup-queue position.
 *
 * OPE-382: this used to write `holdPosition` through the generic `PUT /items/:id`,
 * which enforces none of the queue invariants — that is how a unit ended up with six
 * simultaneous primaries. It now goes through `set-position`, which enforces the depth
 * cap and the single-primary rule (and, as of OPE-382, accepts `null` to clear). Expect
 * a `400` with a business-rule message where the PUT used to succeed silently.
 */
export function useUpdateHoldPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, holdPosition }: { lineItemId: string; holdPosition: HoldPosition | null }) =>
      post<{ success: boolean; message: string }>(`/items/${lineItemId}/set-position`, { position: holdPosition }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] });
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
    },
  });
}
